import { Component, computed, inject, input, OnInit, signal, viewChild } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import { forkJoin } from "rxjs";

import { AdaaHelper } from "../../../core/utils";
import {
  DataTableComponent,
  FormActionButtonsComponent,
  FormCheckboxComponent,
  FormDropdownComponent,
  FormDropdownMultiComponent,
  FormInputComponent,
  FormInputFileComponent,
} from "../../../shared/components";
import { Constants } from "../../../shared/constants/constants";
import { AdaaBoolean, FileInputType, PageMode } from "../../../shared/constants/enums";
import { SmartToolTipsDirective, SmartToolTipsService } from "../../../shared/directives";
import {
  AttachFile,
  EntityModelType,
  EntityOfficeModelType,
  PropTypeModelType,
  TableButtonClicked,
  ValidatorModelType,
  ValueText,
} from "../../../shared/models";
import {
  EntitiesApiService,
  PropertiesService,
  SystemLayoutService,
  UsersApiService,
  ValidatorApiService,
} from "../../../shared/services";
import { AddressesEditorComponent } from "../../entity-profile/addresses/addresses-editor/addresses-editor";

@Component({
  selector: "adaa-entities-editor",
  imports: [
    ReactiveFormsModule,
    FormInputComponent,
    FormCheckboxComponent,
    FormDropdownComponent,
    FormDropdownMultiComponent,
    FormActionButtonsComponent,
    TranslatePipe,
    FormInputFileComponent,
    AddressesEditorComponent,
    DataTableComponent,
    SmartToolTipsDirective,
  ],
  templateUrl: "./entities-editor.html",
  styleUrl: "./entities-editor.scss",
})
export class EntitiesEditorComponent implements OnInit {
  private readonly _entitiesApiService = inject(EntitiesApiService);
  private readonly _usersApiService = inject(UsersApiService);
  private readonly _propsService = inject(PropertiesService);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _modalService = inject(NgbModal);
  private readonly _translateService = inject(TranslateService);
  private readonly _toastrService = inject(ToastrService);
  private readonly _router = inject(Router);
  private readonly _systemLayoutService = inject(SystemLayoutService);
  private readonly _smartToolTipsService = inject(SmartToolTipsService);
  private readonly _validatorApiService = inject(ValidatorApiService);

  private readonly _untilDestroyed = AdaaHelper.untilDestroyed();

  readonly Constants = Constants;

  readonly id = input<number>();
  readonly pageMode = input.required<string>();

  submitted = signal<boolean>(false);
  entity = signal<EntityModelType | undefined>(undefined);
  entityOffices = signal<EntityOfficeModelType[]>([]);
  entityOffice = signal<EntityOfficeModelType | undefined>(undefined);
  addressesPageMode = signal<PageMode>(PageMode.create);
  addressesCurrentIndex = signal<number | undefined>(0);
  formValidation = signal<ValidatorModelType | undefined>(undefined);
  entityTypes = signal<PropTypeModelType[]>([]);
  entitySizes = signal<ValueText[]>([]);
  users = signal<ValueText[]>([]);
  establishedYears = signal<ValueText[]>([]);
  isViewMode = computed(() => this.pageMode() === PageMode.view);
  isEditMode = computed(() => this.pageMode() === PageMode.edit);
  isCreateMode = computed(() => this.pageMode() === PageMode.create);
  entityTypeOptions = computed(() => {
    let entities = this.entityTypes();
    const field = AdaaHelper.getFieldLanguage("name");

    if (
      this.entity()?.entityType === Constants.ENTITY_TYPES.AUTHORITY ||
      this.entity()?.entityType === Constants.ENTITY_TYPES.MINISTRY
    ) {
      entities = entities.filter(
        (entity) => entity.id !== Constants.ENTITY_TYPES.PARTICIPATING && entity.id !== Constants.ENTITY_TYPES.EXTERNAL
      );
    }

    return AdaaHelper.setDropdownArray(entities, "id", field);
  });

  filteredOffices = computed(() => {
    return this.entityOffices().filter((office) => office.status !== Constants.OBJECT_STATUS.REMOVE);
  });

  addressEditor = viewChild.required<AddressesEditorComponent>("editor");

  entitiesForm: FormGroup = new FormGroup({});
  allowedFiles: FileInputType[] = [
    FileInputType.bmp,
    FileInputType.gif,
    FileInputType.jpeg,
    FileInputType.jpg,
    FileInputType.png,
    FileInputType.svg,
  ];

  public get displayLabel(): string {
    const nameEN = this.entitiesForm.get("nameEN")?.value;
    const nameAE = this.entitiesForm.get("nameAE")?.value;

    return AdaaHelper.getPageLabel(nameAE, nameEN);
  }

  public ngOnInit(): void {
    this._loadEntity();
    this._initProps();
    this._prepareForm();

    if (!this.isViewMode()) this._smartToolTipsService.initSmartToolTips(Constants.SMART_TOOL_TIPS_KEY.SMART_ENTITY);

    this._systemLayoutService.hasActiveEntityChanged$.pipe(this._untilDestroyed()).subscribe(() => {
      this._router.navigateByUrl("/console");
    });
  }

  public getImage(direction: "logoHorizontalAttach" | "logoVerticalAttach"): AttachFile {
    const attach = this.entitiesForm.get(direction)?.value;
    return {
      id: attach?.id ?? 0,
      filename: attach?.filename ?? "",
    };
  }

  public getFileMessage(direction: "logoHorizontalAttach" | "logoVerticalAttach"): string {
    return `${this._translateService.instant(direction === "logoHorizontalAttach" ? "entity.create.horizontal_logo_guidelines" : "entity.create.vertical_logo_guidelines")} 
    <br> ${this._translateService.instant("entity.max_file_size")}`;
  }

  private _loadEntity() {
    const id = this.id();
    if (!id) return;

    this._entitiesApiService.getById(id).subscribe({
      next: (response) => {
        const entity = response.responseData;

        this.entity.set(entity);
        this.entitiesForm.patchValue(entity);

        if (
          entity.entityType === Constants.ENTITY_TYPES.EXTERNAL ||
          entity.entityType === Constants.ENTITY_TYPES.PARTICIPATING
        ) {
          this.entitiesForm.get("entityType")?.disable();
          this.entitiesForm.get("enabler")?.disable();
        }

        this.entityOffices.set(entity.entityOffice ?? []);
      },
    });
  }

  private _initProps() {
    const id = this.id();
    const requests = {
      entitySizes: this._propsService.getPropById(Constants.CONSTANT_ENTITY_SIZE),
      entityTypes: this._propsService.getPropById(Constants.CONSTANT_ENTITY_TYPE),
      ...(id ? { users: this._usersApiService.getOwnersPerEntity(id) } : {}),
    };
    forkJoin(requests).subscribe({
      next: (results) => {
        this.entityTypes.set(results.entityTypes.responseData ?? []);
        this.entitySizes.set(
          AdaaHelper.setDropdownArray(results.entitySizes.responseData ?? [], "id", AdaaHelper.getFieldLanguage("name"))
        );

        this.users.set(
          AdaaHelper.setDropdownArray(results.users?.responseData ?? [], "id", AdaaHelper.getFieldLanguage("name"))
        );
      },
    });

    const currentYear = new Date().getFullYear();
    const options: ValueText[] = [];
    for (let index = 1970; index <= currentYear; index++) {
      options.push({ value: index, text: index.toString() });
    }
    this.establishedYears.set(options);
  }

  private _prepareForm() {
    const isViewMode = this.isViewMode();

    this.entitiesForm = this._formBuilder.group({
      nameAE: { value: null, disabled: isViewMode },
      nameEN: { value: null, disabled: isViewMode },
      dscAE: { value: null, disabled: isViewMode },
      dscEN: { value: null, disabled: isViewMode },
      shortNameAE: { value: null, disabled: isViewMode },
      entityType: { value: null, disabled: isViewMode },
      entitySize: { value: null, disabled: isViewMode },
      yearOfEstablishment: { value: null, disabled: isViewMode },
      enabler: { value: null, disabled: isViewMode },
      ministers: { value: null, disabled: isViewMode },
      directorGenerals: { value: null, disabled: isViewMode },
      skpiopmFocalPointId: { value: null, disabled: isViewMode },
      dkpiFocalPointId: { value: null, disabled: isViewMode },
      servicesFocalPointId: { value: null, disabled: isViewMode },
      ekpiFocalPointId: { value: null, disabled: isViewMode },
      strategyManagerId: { value: null, disabled: isViewMode },
      nkpiFocalPointId: { value: null, disabled: isViewMode },
      aiFocalPointId: { value: null, disabled: isViewMode },
      logoHorizontalAttach: null,
      logoVerticalAttach: null,
    });

    this._validatorApiService
      .applyValidationToForm(Constants.VALIDATORS_CONF_KEY.VALIDATION_ENTITY, this.entitiesForm)
      .subscribe({
        next: (response) => {
          this.formValidation.set(response.validation);
          this.entitiesForm = response.form;
        },
      });
  }

  public checkInvalid(control: string) {
    return (
      this.entitiesForm.controls[control]?.errors?.["required"] &&
      (this.entitiesForm.get(control)?.dirty || this.submitted())
    );
  }

  public addNew() {
    this.addressEditor().addressForm.reset({});
    this.addressEditor().addressForm.enable();
    this.addressEditor().markerPositions = [];
    this.addressesPageMode.set(PageMode.create);
    this.addressEditor().open();
    if (
      this.entityOffices().find(
        (office) => office.isHQ === AdaaBoolean.Y && office.status !== Constants.OBJECT_STATUS.REMOVE
      )
    )
      this.addressEditor().addressForm.get("isHQ")?.disable();
  }

  public getAction(value: TableButtonClicked) {
    this.addressesCurrentIndex.set(value.index);
    switch (value.event) {
      case "view":
        this.entityOffice.set(value.data);
        this.addressesPageMode.set(PageMode.view);
        this.addressEditor().addressForm.patchValue(value.data);
        this.addressEditor().addressForm.disable();
        this.addressEditor().open();
        break;
      case "edit":
        this.entityOffice.set(value.data);
        this.addressesPageMode.set(PageMode.edit);
        this.addressEditor().addressForm.patchValue(value.data);
        this.addressEditor().addressForm.enable();
        this.addressEditor().open();
        break;
      case "delete": {
        const offices: EntityOfficeModelType[] = AdaaHelper.clone(this.entityOffices());
        offices[value.index!].status = Constants.OBJECT_STATUS.REMOVE;
        this.entityOffices.set(offices);
        break;
      }

      default:
        break;
    }
  }

  public saveAddress(data: EntityOfficeModelType) {
    const offices: EntityOfficeModelType[] = AdaaHelper.clone(this.entityOffices());
    if (this.addressesPageMode() === PageMode.edit) {
      offices.splice(this.addressesCurrentIndex()!, 1, data);
    } else offices.push(data);
    this.entityOffices.set(offices);
    this._modalService.dismissAll();
  }

  public onFilechange(event: AttachFile | null, direction: "logoHorizontalAttach" | "logoVerticalAttach") {
    if (event) {
      this.entitiesForm.get(direction)?.setValue({
        id: event.id,
        attachFile: null,
        filename: event.filename,
        activityId: null,
        taskId: null,
        auxAttachFile: {
          filename: null,
        },
      });
    } else {
      this.entitiesForm.get(direction)?.setValue(null);
    }
  }

  public submit() {
    this.submitted.set(true);
    if (this.entitiesForm.invalid) {
      this._toastrService.error(this._translateService.instant("notification.warning.missing_info"));
      return;
    }

    const entityOffices = this.entityOffices().map((office) => {
      return {
        geoX: office.geoX,
        geoY: office.geoY,
        entityId: office.entityId,
        addressAE: office.addressAE,
        addressEN: office.addressEN,
        zipCodeAE: office.zipCodeAE,
        cityAE: office.cityAE,
        cityEN: office.cityEN,
        isHQ: office.isHQ,
        id: office.id,
        status: office.status,
        updateTS: office.updateTS,
      };
    });

    const form = this.entitiesForm.getRawValue();

    const result = {
      ...this.entity(),
      ...form,
      entityOffice: [...(entityOffices ?? [])],
      shortNameEN: form.shortNameAE,
      status: Constants.OBJECT_STATUS.ACTIVE,
      ...(form.logoHorizontalAttach === null && {
        logoHorizontalAttach: {
          id: null,
          attachFile: null,
          filename: null,
          activityId: null,
          taskId: null,
          auxAttachFile: {
            filename: null,
          },
        },
      }),
      ...(form.logoVerticalAttach === null && {
        logoVerticalAttach: {
          id: null,
          attachFile: null,
          filename: null,
          activityId: null,
          taskId: null,
          auxAttachFile: {
            filename: null,
          },
        },
      }),
    };

    if (this.pageMode() === PageMode.create) {
      delete result.directorGenerals;
      delete result.ministers;
    }

    this._entitiesApiService.saveEntity(result, this.pageMode() === PageMode.edit).subscribe({
      next: (response) => {
        if (response.inError) return;
        this._toastrService.success(this._translateService.instant("notification.success.save"));
        this.close();
      },
    });
  }

  public close() {
    this._router.navigateByUrl("/console/entities");
  }
}
